import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  acceptAll,
  applyRegionalConsentDefault,
  getConsent,
  hasDecided,
  isGpcEnabled,
  OPEN_PREFS_EVENT,
  rejectNonEssential,
  setConsent,
} from "@/lib/consent";
import { isGdprCountry } from "@/lib/geo";
import { CONSENT_T, getLang, setLang, type Lang } from "@/lib/i18n/consent";

export const CookieConsent = () => {
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [draft, setDraft] = useState({ analytics: false, functional: false, advertising: false });
  const [lang, setLangState] = useState<Lang>("en");

  const t = CONSENT_T[lang];

  useEffect(() => {
    setLangState(getLang());
    // Region-aware default: EU/UK visitors get the opt-in banner; everyone
    // else is auto-opted-in. GPC always wins. Banner only shows for undecided
    // visitors in GDPR regions.
    void applyRegionalConsentDefault().then((country) => {
      if (!hasDecided() && isGdprCountry(country)) setBannerOpen(true);
    });
    const onLang = () => setLangState(getLang());
    window.addEventListener("cws:lang-changed", onLang);
    return () => window.removeEventListener("cws:lang-changed", onLang);
  }, []);


  useEffect(() => {
    const open = () => {
      const c = getConsent();
      setDraft({
        analytics: c?.analytics ?? true,
        functional: c?.functional ?? true,
        advertising: c?.advertising ?? true,
      });
      setPrefsOpen(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, open);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, open);
  }, []);


  const handleAcceptAll = () => {
    acceptAll();
    setPrefsOpen(false);
    setBannerOpen(false);
    toast.success(t.toastSavedAll);
  };
  const handleRejectAll = () => {
    rejectNonEssential();
    setPrefsOpen(false);
    setBannerOpen(false);
    toast(t.toastSavedNone);
  };
  const handleSavePrefs = () => {
    setConsent(draft);
    setPrefsOpen(false);
    setBannerOpen(false);
    toast.success(t.toastSavedCustom);
  };

  const openManage = () => {
    const c = getConsent();
    setDraft({
      analytics: c?.analytics ?? false,
      functional: c?.functional ?? false,
      advertising: c?.advertising ?? false,
    });
    setPrefsOpen(true);
  };

  const LangToggle = () => (
    <div className="inline-flex items-center gap-1 text-[11px] font-semibold">
      <button
        type="button"
        onClick={() => setLang("en")}
        className={`inline-flex min-h-6 min-w-6 items-center justify-center rounded px-2 py-1 ${lang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
        aria-pressed={lang === "en"}
      >EN</button>
      <button
        type="button"
        onClick={() => setLang("es")}
        className={`inline-flex min-h-6 min-w-6 items-center justify-center rounded px-2 py-1 ${lang === "es" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
        aria-pressed={lang === "es"}
      >ES</button>
    </div>
  );

  const cats = [
    { key: "necessary" as const, alwaysOn: true, ...t.cats.necessary },
    { key: "analytics" as const, alwaysOn: false, ...t.cats.analytics },
    { key: "functional" as const, alwaysOn: false, ...t.cats.functional },
    { key: "advertising" as const, alwaysOn: false, ...t.cats.advertising },
  ];

  return (
    <>
      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>

        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>{t.prefsTitle}</DialogTitle>
              <LangToggle />
            </div>
            <DialogDescription>{t.prefsDesc}</DialogDescription>
          </DialogHeader>

          {isGpcEnabled() && (
            <p className="rounded border border-border bg-muted/50 p-2 text-[12px] text-muted-foreground">
              {t.gpcDetected}
            </p>
          )}

          <div className="space-y-4">
            {cats.map((cat) => {
              const checked = cat.alwaysOn ? true : draft[cat.key as "analytics" | "functional" | "advertising"];
              return (
                <div key={cat.key} className="flex items-start justify-between gap-4 rounded-md border border-border bg-card p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{cat.title}</p>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{cat.desc}</p>
                    {cat.alwaysOn && (
                      <p className="mt-1 text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{t.alwaysOn}</p>
                    )}
                  </div>
                  <Switch
                    checked={checked}
                    disabled={cat.alwaysOn}
                    onCheckedChange={(v) =>
                      !cat.alwaysOn &&
                      setDraft((d) => ({ ...d, [cat.key]: v }))
                    }
                    aria-label={cat.title}
                  />
                </div>
              );
            })}
          </div>

          <p className="text-[12px] text-muted-foreground">
            <Link to="/privacy" className="underline hover:text-foreground">{t.privacy}</Link>
            <span className="mx-1.5">·</span>
            <Link to="/legal/cookies" className="underline hover:text-foreground">{t.cookies}</Link>
            <span className="mx-1.5">·</span>
            <Link to="/legal/accessibility" className="underline hover:text-foreground">{t.accessibility}</Link>
          </p>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={handleRejectAll}>{t.rejectAll}</Button>
            <Button variant="outline" onClick={handleAcceptAll}>{t.acceptAll}</Button>
            <Button onClick={handleSavePrefs}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EU/UK first-visit opt-in banner */}
      {bannerOpen && (
        <div
          role="dialog"
          aria-label={t.title}
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-3xl rounded-lg border border-border bg-card/95 p-4 shadow-2xl backdrop-blur md:left-auto md:right-3 md:mx-0"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="mb-1 flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-foreground">{t.title}</p>
                <LangToggle />
              </div>
              <p className="text-[12.5px] leading-relaxed text-muted-foreground">{t.body}</p>
              <p className="mt-1 text-[11.5px] text-muted-foreground">
                <Link to="/privacy" className="underline hover:text-foreground">{t.privacy}</Link>
                <span className="mx-1.5">·</span>
                <Link to="/legal/cookies" className="underline hover:text-foreground">{t.cookies}</Link>
              </p>
            </div>
            <div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row md:flex-col md:items-stretch">
              <Button size="sm" onClick={handleAcceptAll}>{t.acceptAll}</Button>
              <Button size="sm" variant="outline" onClick={handleRejectAll}>{t.rejectAll}</Button>
              <Button size="sm" variant="ghost" onClick={openManage}>{t.manage}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
